import React, { createContext, useContext, useState, useMemo } from 'react';
import axios from 'axios';
import rawsIconColored from '../assets/icons/raws-icon-colored.png';
import predictionIcon from '../assets/icons/prediction-stroke.png';
// import rawsIconColored from '../assets/icons/raws-icon-background.png'
import { usePanelContext } from './PanelContext';
import mapboxgl from 'mapbox-gl';
import { useUserContext } from './UserContext.js';
import { useAreasContext } from './AreasContext.js';
import planeIcon from '../assets/icons/plane-icon-2.png';
import bearing from '@turf/bearing';
import * as s from "satellite.js";
import satelliteIcon from '../assets/icons/satellite-icon-2.png';

const SensorDataContext = createContext();

export const SensorDataContextProvider = ({ children }) => {
    const {
        area
    } = useAreasContext();

    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        setPanel
    } = usePanelContext();

    const [raws, setRaws] = useState(null);
    const [viirs, setViirs] = useState(null);
    const [modis, setModis] = useState(null);
    const [predictions, setPredictions] = useState(null);
    const [satelliteLayers, setSatelliteLayers] = useState(null);
    const [selectedSatelliteLayer, setSelectedSatelliteLayer] = useState(null);
    const [satellites, setSatellites] = useState(null);
    const [flights, setFlights] = useState(null);
    
    const [layers, setLayers] = useState({
        predictions: false,
        raws: false,
        viirs: true,
        modis: true,
        satellites: false,
        flights: false
    });

    const encodeURL = (bounds) => {
        const urlFormat = `%7B%22rings%22%3A%5B%5B${bounds.map((set) => `%5B${set[0]},${set[1]}%5D`).toString().replaceAll(',', '%2C')}%5D%5D%2C%22spatialReference%22%3A%7B%22wkid%22%3A+4326%7D%7D`;
        return urlFormat;
    }

    const getBounds = (rings) => {
        let min = [rings[0][0], rings[0][1]]
        let max = [rings[0][0], rings[0][1]]

        rings.forEach((pair) => {
            if(pair[0] < min[0]) min = [pair[0], min[1]]
            if(pair[1] < min[1]) min = [min[0], pair[1]]
            if(pair[0] > max[0]) max = [pair[0], max[1]]
            if(pair[1] > min[1]) max = [max[0], pair[1]]
        });

        const bounds = [[min[0], max[1]], [min[0], min[1]], [max[0], min[1]], [max[0], max[1]]];
        return bounds;
    }

    const WKTtoRings = (wkt) => {
        const rings = wkt.slice(9).slice(0, -2).split(',').map((coordinates) => {
            return coordinates.split(' ').filter((element) => element !== '').map((element) => parseFloat(element));
        });
        return rings;
    };

    const plotPredictions = (map, data) => {
        if (!map?.current) {
            console.warn('plotPredictions: Map not initialized, skipping plot');
            return;
        }
        // Check if map style is loaded (required for getStyle() to work)
        if (!map.current.isStyleLoaded()) {
            console.warn('plotPredictions: Map style not loaded yet, waiting...');
            map.current.once('style.load', () => {
                plotPredictions(map, data);
            });
            return;
        }
        const layers = map.current.getStyle().layers?.filter(layer => layer.id.includes('Layer-Prediction-symbol')) || [];

        if (layers.length > 0)
            layers.forEach((layer) => map.current.removeLayer(layer.id));

        const source = map.current.getStyle().sources['Source-Prediction'];

        if (source)
            map.current.removeSource('Source-Prediction');

        if (map.current.hasImage('prediction-icon')) {
            map.current.removeImage('prediction-icon');
        }

        map.current.loadImage(predictionIcon, function(error, image) {
            if (error) throw error;

            map.current.addImage('prediction-icon', image);

            map.current.addSource(`Source-Prediction`,
                {
                    "type": "geojson",
                    "data": {
                        "type": "FeatureCollection",
                        "features": 
                        data.map((prediction) => {
                                return {
                                    "type": "Feature",
                                    "properties": prediction,
                                    "geometry": {
                                        "type": "Point",
                                        "coordinates": [prediction.longitude, prediction.latitude]
                                    }
                                }
                            })
                        
                    }
                }
            );

            map.current.addLayer({
                "id": `Layer-Prediction-symbol`,
                "type": "symbol",
                "source": `Source-Prediction`,
                "layout": {
                    "icon-image": "prediction-icon",
                    "icon-size": 0.6,
                    'icon-allow-overlap': true,
                    'icon-ignore-placement': true,
                    "visibility": 'none',
                }
            });

            const layers = map.current.getStyle().layers.filter((layer) => layer.id === 'RAWS Station Layer');

            if (layers.length > 0)
                map.current.moveLayer('RAWS Station Layer');

            map.current.on('click', 'Layer-Prediction-symbol', (e) => {
                const features = map.current.queryRenderedFeatures(e.point, {
                    layers: ['Layer-Prediction-symbol']
                });
            
                if (!features.length) {
                    return;
                }
            
                const feature = features[0];
                const props = feature.properties;
                const location = [props.longitude, props.latitude];

                // Create popup similar to fire popup
                const popup = new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                .setLngLat(location)
                .setHTML(`
                    <div>
                        <div class='alert-popup-title'>Wildifire Prediction (${props.fire_id})</div>
                        <div class='alert-popup-row'><div class='alert-popup-row-name'>ID:</div><div class='alert-popup-row-value'>${props.fire_id}</div></div>
                        <div class='alert-popup-row'><div class='alert-popup-row-name'>Country:</div><div class='alert-popup-row-value'>${props.country || 'Not Available'}</div></div>
                        <div class='alert-popup-row'><div class='alert-popup-row-name'>Coordinates:</div><div class='alert-popup-row-value'>${location[1].toFixed(5)}, ${location[0].toFixed(5)}</div></div>
                        <div class='alert-popup-row-button js-view-prediction'>View Prediction</div>
                    </div>
                `)
                .addTo(map.current);

                const btn = popup.getElement().querySelector('.js-view-prediction');
                btn.style.cursor = 'pointer';
                btn.addEventListener('click', () => {
                    popup.remove();
                    setPanel({
                        name: 'Prediction PopUp',
                        content: props
                    });

                    const location = [props.longitude, props.latitude]
                    map.current.flyTo({center: location, zoom: 11, pitch: 0, offset: [250, 0]});
                });
            });

            // Change the cursor to a pointer when over the layer
            map.current.on('mouseenter', 'Layer-Prediction-symbol', () => {
                map.current.getCanvas().style.cursor = 'pointer';
            });
        
            // Change it back to default when leaving the layer
            map.current.on('mouseleave', 'Layer-Prediction-symbol', () => {
                map.current.getCanvas().style.cursor = '';
            });
        });
    }

    const getPredictions = (map, token, setRefreshingCallback) => {
        if (setRefreshingCallback) {
            setRefreshingCallback(true);
        }

        axios.get('https://firewatch-api.space-eyes.com/predictions', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).then((response) => {
            if (response.data.predictions.length > 0) {
                setPredictions(response.data.predictions);
                plotPredictions(map, response.data.predictions);
            } else {
                setPredictions([]);
                plotPredictions(map, []);
            }

            if (setRefreshingCallback){
                setRefreshingCallback(false);
            }
        }).catch((err) => {
            console.log('error gettting predictions', err);

            if (setRefreshingCallback){
                setRefreshingCallback(false);
            }
        })
    }

    const getRAWS = (config, map, user) => {
        if (config && config !== 'error' && map.current) {
            axios.get(`https://firewatch-api.space-eyes.com/weather/raws`, {
                headers: {
                    'Authorization': `Bearer ${user.idToken.jwtToken}`
                }
            })
            .then((response) => {
                console.log('raws', response.data.stations)
                setRaws(response.data.stations);
                plotRaws(map, response.data.stations);

            }).catch((err) => {
                console.log('error calling RAWS api', err);
            })
        }
    }

    const plotRaws = (map, stations) => {
        if (!map?.current) {
            console.warn('plotRaws: Map not initialized, skipping plot');
            return;
        }
        // Check if map style is loaded (required for getStyle() to work)
        if (!map.current.isStyleLoaded()) {
            console.warn('plotRaws: Map style not loaded yet, waiting...');
            map.current.once('style.load', () => {
                plotRaws(map, stations);
            });
            return;
        }
        const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes('Layer-RAWS'));
        if(layers.length > 0) {
            layers.forEach((layer) => map.current.removeLayer(layer.id))
        }

        const source = map.current.getStyle().sources['Source-RAWS'];

        if (source)
            map.current.removeSource('Source-RAWS');
        
        if (map.current.hasImage('raws-icon')) {
            map.current.removeImage('raws-icon');
        }

        map.current.loadImage(rawsIconColored, function(error, image) {
            if (error) throw error;

            map.current.addImage('raws-icon', image);

            map.current.addSource('Source-RAWS',
                {
                    "type": "geojson",
                    "data": {
                        "type": "FeatureCollection",
                        "features": 
                            stations.map((station) => {
                                return {
                                    "type": "Feature",
                                    "properties": station,
                                    "geometry": {
                                        "type": "Point",
                                        "coordinates": [station.longitude, station.latitude]
                                    }
                                }
                            })
                        
                    }
                }
            );

            map.current.addLayer({
                "id": 'Layer-RAWS-symbol',
                "type": "symbol",
                "source": 'Source-RAWS',
                "layout": {
                    "icon-image": "raws-icon",
                    "icon-size": 0.40,
                    "visibility": 'none',
                },
            });

            map.current.on('click', 'Layer-RAWS-symbol', (e) => {
                const features = map.current.queryRenderedFeatures(e.point, {
                    layers: ['Layer-RAWS-symbol']
                });
            
                if (!features.length) {
                    return;
                }
            
                const feature = features[0];
                const location = [feature.properties.longitude,feature.properties.latitude]
            
                map.current.flyTo({center: location, zoom: 11, pitch: 0, offset: [250, 0]});

                if (is3D) {
                    setIs3D(false);
                }

                setPanel({
                    name: 'RAWS - Singular',
                    content: feature.properties
                })
            });
        
            map.current.on('mouseenter', 'Layer-RAWS-symbol', () => {
                map.current.getCanvas().style.cursor = 'pointer';
            });
           
            map.current.on('mouseleave', 'Layer-RAWS-symbol', () => {
                map.current.getCanvas().style.cursor = '';
            });
        });
    }

    const getMODIS = (map) => {
        if (!map?.current) {
            console.warn('getMODIS: Map not initialized, skipping');
            return;
        }
        // Check if map style is loaded (required for getStyle() to work)
        if (!map.current.isStyleLoaded()) {
            console.warn('getMODIS: Map style not loaded yet, waiting...');
            map.current.once('style.load', () => {
                getMODIS(map);
            });
            return;
        }
        const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes('Layer-MODIS'));
        if(layers.length > 0) {
            layers.forEach((layer) => map.current.removeLayer(layer.id))
        }

        if(map.current.getStyle().sources['Source-MODIS'])
            map.current.removeSource('Source-MODIS');

        const rings = WKTtoRings(area.wkt);
        const urlFormat = encodeURL(getBounds(rings));
    

        axios.get(`https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services/MODIS_Thermal_v1/FeatureServer/0/query?where=HOURS_OLD+<+72&objectIds=&time=&geometry=${urlFormat}&geometryType=esriGeometryPolygon&inSR=&spatialRel=esriSpatialRelIntersects&resultType=none&distance=0.0&units=esriSRUnit_Meter&relationParam=&returnGeodetic=false&outFields=*&returnGeometry=true&featureEncoding=esriDefault&multipatchOption=xyFootprint&maxAllowableOffset=&geometryPrecision=&outSR=&defaultSR=&datumTransformation=&applyVCSProjection=false&returnIdsOnly=false&returnUniqueIdsOnly=false&returnCountOnly=false&returnExtentOnly=false&returnQueryGeometry=false&returnDistinctValues=false&cacheHint=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&having=&resultOffset=&resultRecordCount=&returnZ=false&returnM=false&returnExceededLimitFeatures=true&quantizationParameters=&sqlFormat=none&f=pgeojson`)
        .then((response) => {
            let data = response.data;
            setModis(response.data.features);

            map.current.addSource('Source-MODIS', {
                type: 'geojson',
                data: data
            });

            map.current.addLayer(
                {
                    id: 'Layer-MODIS-heatmap',
                    type: 'heatmap',
                    source: 'Source-MODIS',
                    minzoom: 3,
                    maxzoom: 16,
                    layout: {
                        visibility: "visible"
                    },
                    paint: {
                        'heatmap-weight': {
                            property: 'bright_ti4',
                            type: 'exponential',
                            stops: [
                                [1, 0],
                                [62, 0.8] // Reduced intensity
                            ]
                        },
                        'heatmap-intensity': {
                            stops: [
                                [11, 1], // Lowered intensity
                                [15, 2.5]
                            ]
                        },
                        'heatmap-color': [
                            'interpolate',
                            ['linear'],
                            ['heatmap-density'],
                            0,
                            'rgba(255,245,240,0)',
                            0.2,
                            'rgb(255, 225, 0)',
                            0.4,
                            'rgb(255, 119, 0)',
                            0.6,
                            'rgb(255, 157, 0)',
                            0.8,
                            'rgb(255, 119, 0)',
                        ],
                        'heatmap-radius': {
                            stops: [
                                [11, 10], // Reduced radius
                                [15, 15]
                            ]
                        },
                        'heatmap-opacity': {
                            default: 1, // Reduced opacity
                            stops: [
                                [14, 0.9],
                                [15, 0] // Fades out gradually
                            ]
                        }
                    }                                         
                },
                'waterway-label'
            );

            map.current.addLayer(
                {
                    id: 'Layer-MODIS-point',
                    type: 'circle',
                    source: 'Source-MODIS',
                    minzoom: 16,
                    layout: {
                        visibility: "visible"
                    },
                    paint: {
                        'circle-radius': 8,
                        'circle-color': 'rgb(255, 225, 0)',
                        'circle-stroke-color': 'white',
                        'circle-stroke-width': 1
                    }                   
                },
                'waterway-label'
            );

            map.current.on('click', 'Layer-MODIS-point', (event) => {
                new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                  .setLngLat(event.features[0].geometry.coordinates)
                  .setHTML(`<div>
                        <div class='alert-popup-title'>MODIS Thermal Hotspots</div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Time of collection:</div>
                            <div class='alert-popup-row-value'>${new Date(event.features[0].properties.ACQ_DATE).toLocaleDateString()}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Time of collection:</div>
                            <div class='alert-popup-value'>${event.features[0].properties.BRIGHTNESS} K</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Satellite:</div>
                            <div class='alert-popup-row-value'>${event.features[0].properties.SATELLITE}</div>
                        </div>
                    </div>`)
                  .addTo(map.current);
            });
        }).catch((err) => {
            console.log(err);
        });
    }

    const getVIIRS = (map) => {
        if (!map?.current) {
            console.warn('getVIIRS: Map not initialized, skipping');
            return;
        }
        // Check if map style is loaded (required for getStyle() to work)
        if (!map.current.isStyleLoaded()) {
            console.warn('getVIIRS: Map style not loaded yet, waiting...');
            map.current.once('style.load', () => {
                getVIIRS(map);
            });
            return;
        }
        const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes('VIIRS'));
        if(layers.length > 0) {
            layers.forEach((layer) => map.current.removeLayer(layer.id))
        }

        if(map.current.getStyle().sources['Source-VIIRS'])
            map.current.removeSource('Source-VIIRS');

        const rings = WKTtoRings(area.wkt);
        const urlFormat = encodeURL(getBounds(rings));

        axios.get(`https://services9.arcgis.com/RHVPKKiFTONKtxq3/ArcGIS/rest/services/Satellite_VIIRS_Thermal_Hotspots_and_Fire_Activity/FeatureServer/0/query?where=hours_old+<+100&objectIds=&time=&geometry=${urlFormat}&geometryType=esriGeometryPolygon&inSR=&spatialRel=esriSpatialRelIntersects&resultType=none&distance=0.0&units=esriSRUnit_Meter&relationParam=&returnGeodetic=false&outFields=*&returnGeometry=true&featureEncoding=esriDefault&multipatchOption=xyFootprint&maxAllowableOffset=&geometryPrecision=&outSR=&defaultSR=&datumTransformation=&applyVCSProjection=false&returnIdsOnly=false&returnUniqueIdsOnly=false&returnCountOnly=false&returnExtentOnly=false&returnQueryGeometry=false&returnDistinctValues=false&cacheHint=false&orderByFields=&groupByFieldsForStatistics=&outStatistics=&having=&resultOffset=&resultRecordCount=&returnZ=false&returnM=false&returnExceededLimitFeatures=true&quantizationParameters=&sqlFormat=none&f=pgeojson`)
        .then((response) => {
            const data = response.data;
            setViirs(response.data.features);
            map.current.addSource('Source-VIIRS', {
                type: 'geojson',
                data: data
            });

            map.current.addLayer(
                {
                    id: 'Layer-VIIRS-heatmap',
                    type: 'heatmap',
                    source: 'Source-VIIRS',
                    minzoom: 3,
                    maxzoom: 16,
                    layout: {
                        visibility: "visible"
                    },
                    paint: {
                        'heatmap-weight': {
                            property: 'bright_ti4',
                            type: 'exponential',
                            stops: [
                                [1, 0],
                                [62, 0.8] // Reduced intensity
                            ]
                        },
                        'heatmap-intensity': {
                            stops: [
                                [11, 1], // Lowered intensity
                                [15, 2.5]
                            ]
                        },
                        'heatmap-color': [
                            'interpolate',
                            ['linear'],
                            ['heatmap-density'],
                            0,
                            'rgba(255,245,240,0)',
                            0.2,
                            'rgb(255, 225, 0)',
                            0.4,
                            'rgb(255, 119, 0)',
                            0.6,
                            'rgb(255, 157, 0)',
                            0.8,
                            'rgb(255, 119, 0)',
                        ],
                        'heatmap-radius': {
                            stops: [
                                [11, 10], // Reduced radius
                                [15, 15]
                            ]
                        },
                        'heatmap-opacity': {
                            default: 1, // Reduced opacity
                            stops: [
                                [14, 0.9],
                                [15, 0] // Fades out gradually
                            ]
                        }
                    }                               
                },
                'waterway-label'
            );

            map.current.addLayer(
                {
                    id: 'Layer-VIIRS-point',
                    type: 'circle',
                    source: 'Source-VIIRS',
                    minzoom: 16,
                    layout: {
                        visibility: "visible"
                    },
                    paint: {
                        'circle-radius': 8,
                        'circle-color': 'rgb(255, 225, 0)',
                        'circle-stroke-color': 'white',
                        'circle-stroke-width': 1
                    }                    
                },
                'waterway-label'
            );

            map.current.on('click', 'Layer-VIIRS-point', (event) => {
                console.log(event.features[0])
                new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                  .setLngLat(event.features[0].geometry.coordinates)
                  .setHTML(`<div>
                        <div class='alert-popup-title'>VIIRS Thermal Hotspots</div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Time of collection:</div>
                            <div class='alert-popup-row-value'>${new Date(event.features[0].properties.acq_date).toLocaleDateString()}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Brightness:</div>
                            <div class='alert-popup-row-value'>${event.features[0].properties.bright_ti4} K</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Satellite:</div>
                            <div class='alert-popup-row-value'>${event.features[0].properties.satellite}</div>
                        </div>
                    </div>`)
                  .addTo(map.current);
            });
        }).catch((err) => {
            console.log(err);
        });
    }

    const plotFlights = (map, token) => {
        axios.get('https://firewatch-api.space-eyes.com/flights', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).then((response) => {
            setFlights(response.data.flights);

            if (map.current.hasImage('plane-icon')) {
                map.current.removeImage('plane-icon');
            }

            const flightLayers = map.current.getStyle().layers.filter(layer =>
                layer.id.includes(`flights`)
            );
            if (flightLayers.length > 0) {
                flightLayers.forEach(layer => map.current.removeLayer(layer.id));
            }
            
            if (map.current.getStyle().sources[`Source-flights`]) {
                map.current.removeSource(`Source-flights`);
            }

            const validFlights = response.data.flights.filter(f => f.path && f.path.startsWith("LINESTRING("));

            const parseWKTLine2 = wkt => {
                const coords = wkt
                    .slice(11, -1)
                    .split(",")
                    .map(pair =>
                    pair.trim().split(/\s+/).map(Number)
                    );
                return {
                    type: "LineString",
                    coordinates: coords
                };
                };

            const iconFeatures = validFlights.map(flight => {
                const coords = parseWKTLine2(flight.path).coordinates;
                const lastCoord = coords[coords.length - 1];
                const prevCoord = coords[coords.length - 2] || lastCoord;
                return {
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: lastCoord },
                    properties: {
                    bearing: bearing(prevCoord, lastCoord),
                    ...flight
                    }
                };
                });
                
                const pointGeojson = {
                    type: 'FeatureCollection',
                    features: iconFeatures
                };                              
            
            const features = validFlights.map(flight => ({
                type: 'Feature',
                geometry: parseWKTLine2(flight.path),
                properties: {
                    trip_id:   flight.trip_id,
                    icao24:    flight.icao24,
                    callsign:  flight.callsign,
                    startTime: flight.start_time,
                    endTime:   flight.end_time,
                    active:    flight.active
                }
            }));

            console.log(features)
            
            const geojson = {
                type: 'FeatureCollection',
                features
            };

            console.log(geojson)

            if (!map.current.hasImage('plane-icon')) {
                map.current.loadImage(planeIcon, (error, image) => {
                    if (error) throw error;
        
                    map.current.addImage('plane-icon', image);
                });

                map.current.addSource('Source-flights-icons-point', {
                    type: 'geojson',
                    data: pointGeojson
                    });

                    map.current.addLayer({
                    id: 'Layer-flights-icons',    // ← plain ASCII hyphens here
                    type: 'symbol',
                    source: 'Source-flights-icons-point',
                    layout: {
                        'icon-image':            'plane-icon',
                        'icon-size':             0.4,
                        'icon-rotate': ['-', ['get', 'bearing'], 90],
                        'icon-rotation-alignment':'map',
                        'icon-keep-upright':     true,
                        'icon-allow-overlap':    true,
                        'icon-ignore-placement': true,
                        "visibility": 'none',
                    }
                    });
        

                map.current.addSource("Source-flights", {
                    type: "geojson",
                    data: geojson
                });

                map.current.addLayer({
                    id: 'Layer-flights',
                    type: 'line',
                    source: 'Source-flights',
                    layout: {
                    'line-join': 'round',
                    'line-cap':  'round',
                    "visibility": 'none',
                    },
                    paint: {
                    'line-color': 'rgba(0, 255, 38, 0.8)',
                    'line-width':  3
                    }
                });

                // keep a reference so we can remove the old popup
                let currentPopup = null;

                map.current.on('click', 'Layer-flights-icons', (e) => {
                // e.features is populated because we specified the layer in the click listener
                if (!e.features || !e.features.length) return;
                const feature = e.features[0];
                console.log('hello')

                // 1️⃣ get the [lng, lat] for the popup
                const coords = feature.geometry.coordinates;

                // 2️⃣ unpack the properties you care about
                const { icao24, callsign, active } = feature.properties;

                // 3️⃣ remove any existing popup
                if (currentPopup) currentPopup.remove();

                // 4️⃣ build & show the new popup
                currentPopup = new mapboxgl.Popup({
                    offset: 25,
                    maxWidth: '300px',
                    className: 'popup'
                    })
                    .setLngLat(coords)
                    .setHTML(`
                    <div>
                        <div class='alert-popup-title'>Callsign: ${callsign || 'Unknown callsign'}</div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>Status:</div>
                            <div class='alert-popup-row-value'>${active === 'true' ? 'ACTIVE' : 'INACTIVE'}</div>
                        </div>
                        <div class='alert-popup-row'>
                            <div class='alert-popup-row-name'>ICA024:</div>
                            <div class='alert-popup-row-value'>${icao24 ?icao24.toUpperCase() : 'Unknown' }</div>
                        </div>
                    </div>
                    `)
                    .addTo(map.current);
                });

                // change cursor on hover
                map.current.on('mouseenter', 'Layer-flights-icons', () => {
                map.current.getCanvas().style.cursor = 'pointer';
                });
                map.current.on('mouseleave', 'Layer-flights-icons', () => {
                map.current.getCanvas().style.cursor = '';
                });
            }
        }).catch((err) => console.log('error getting flights', err));
    }

    const plotSatellites = (map, token) => {
        axios.get('https://firewatch-api.space-eyes.com/satellites', {
        headers: { 'Authorization': `Bearer ${token}` }
        }).then((response) => {
        setSatellites(response.data.satellites);
        console.log('api called sat')
        if (map.current.hasImage('satellite-icon')) {
            map.current.removeImage('satellite-icon');
        }
        map.current.getStyle().layers
            .filter(l => l.id.includes('satellites'))
            .forEach(l => map.current.removeLayer(l.id));
        if (map.current.getStyle().sources['Source-satellites-icons-point']) {
            map.current.removeSource('Source-satellites-icons-point');
        }

        const TLEtoCoords = (TLE, when) => {
            const satrec = s.twoline2satrec(TLE[0], TLE[1]);
            const { position } = s.propagate(satrec, when);
            const gmst = s.gstime(when);
            const geo = s.eciToGeodetic(position, gmst);
            const lat = s.degreesLat(geo.latitude);
            const lon = s.degreesLong(geo.longitude);
            return [parseFloat(lon.toFixed(6)), parseFloat(lat.toFixed(6))];
        };
    
        const sats = response.data.satellites;
    
        const buildFeatures = () => {
            const now = new Date();
            return sats.map(sat => {
            const [lon, lat] = TLEtoCoords(sat.tle, now);
            return {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [lon, lat] },
                properties: sat
            };
            });
        };
    
        // load icon, add source + layer, then start interval
        map.current.loadImage(satelliteIcon, (error, image) => {
            if (error) throw error;
            map.current.addImage('satellite-icon', image);
    
            // initial draw
            map.current.addSource('Source-satellites-icons-point', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: buildFeatures()
            }
            });
            map.current.addLayer({
            id: 'Layer-satellites-icons',
            type: 'symbol',
            source: 'Source-satellites-icons-point',
            maxzoom: 3,
            layout: {
                'icon-image':            'satellite-icon',
                'icon-size':             0.4,
                'icon-allow-overlap':    true,
                'icon-ignore-placement': true,
                "visibility": 'none',
                
            }
            });
    
            // ▶️ update positions every 2 seconds
            const intervalId = setInterval(() => {
            const updated = {
                type: 'FeatureCollection',
                features: buildFeatures()
            };
            // Add safety check to prevent setData errors when source doesn't exist
            if (map.current && map.current.getSource('Source-satellites-icons-point')) {
                map.current.getSource('Source-satellites-icons-point').setData(updated);
            }
            }, 500);
    
            // keep a reference so we can remove the old popup
            let currentPopup = null;

            map.current.on('click', 'Layer-satellites-icons', (e) => {
            // e.features is populated because we specified the layer in the click listener
            if (!e.features || !e.features.length) return;
            const feature = e.features[0];

            // 1️⃣ get the [lng, lat] for the popup
            const coords = feature.geometry.coordinates;

            // 2️⃣ unpack the properties you care about
            console.log(feature.properties)
            const { type, name, norad_id } = feature.properties;

            // 3️⃣ remove any existing popup
            if (currentPopup) currentPopup.remove();

            // 4️⃣ build & show the new popup
            currentPopup = new mapboxgl.Popup({
                offset: 25,
                maxWidth: '300px',
                className: 'popup'
                })
                .setLngLat(coords)
                .setHTML(`
                <div>
                    <div class='alert-popup-title'>Satellite: ${name || 'Unknown callsign'}</div>
                    <div class='alert-popup-row'>
                        <div class='alert-popup-row-name'>Type:</div>
                        <div class='alert-popup-row-value'>${type || 'Unknown'}</div>
                    </div>
                    <div class='alert-popup-row'>
                        <div class='alert-popup-row-name'>NORAD ID:</div>
                        <div class='alert-popup-row-value'>${norad_id || 'Unknown'}</div>
                    </div>
                </div>
                `)
                .addTo(map.current);
            });

            // change cursor on hover
            map.current.on('mouseenter', 'Layer-satellites-icons', () => {
            map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'Layer-satellites-icons', () => {
            map.current.getCanvas().style.cursor = '';
            });
        });
        }).catch(err => console.log('error fetching satellites', err));
    }

    const getSatelliteLayers = (t) => {
        axios.get('https://firewatch-api.space-eyes.com/predictive-maps', {
            headers: {
                'Authorization': `Bearer ${t}`
            }
        })
        .then((response) => {
            setSatelliteLayers(response.data.tilesets.map((data) => {
                return {
                    id: data.id,
                    created_timestamp: data.created_timestamp,
                    name: data.name,
                    url: data.url,
                    visible: false
                }
            }));
        }).catch((err) => console.log(err));
    } 

    const value = useMemo(() => ({
        predictions,
        setPredictions,
        getPredictions,
        raws,
        setRaws,
        getRAWS,
        viirs,
        modis,
        layers,
        setLayers,
        setViirs,
        setModis,
        getVIIRS,
        getMODIS,
        satellites,
        flights,
        satelliteLayers,
        setSatelliteLayers,
        selectedSatelliteLayer,
        setSelectedSatelliteLayer,
        plotFlights,
        plotSatellites,
        getSatelliteLayers,
    }), [predictions, raws, viirs, modis, layers, satelliteLayers, satellites, flights, selectedSatelliteLayer]);
  
    return (
        <SensorDataContext.Provider 
            value={value}
        >
        {children}
        </SensorDataContext.Provider>
    );
    };

export const useSensorDataContext = () => useContext(SensorDataContext);
